import type {
  CashierOrder, CashierOrderLine, CashierTender, CashierCatalogItem,
} from "./types";

/** Format minor units (kuruş) → display string. 87000 → "870,00" */
export function formatMinor(minor: number, locale: "tr" | "en" = "tr"): string {
  const major = minor / 100;
  return major.toLocaleString(locale === "tr" ? "tr-TR" : "en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatMoney(minor: number, currencyPrefix = "₺", locale: "tr" | "en" = "tr"): string {
  const sign = minor < 0 ? "-" : "";
  return `${sign}${currencyPrefix}${formatMinor(Math.abs(minor), locale)}`;
}

/** Compute order totals from lines. Pure recompute — handy for optimistic UI. */
export function recomputeOrder(order: CashierOrder): CashierOrder {
  const subtotalMinor = order.lines.reduce(
    (acc, l) => acc + l.qty * l.unitPriceMinor,
    0,
  );
  const lineDiscountMinor = order.lines.reduce(
    (acc, l) => acc + (l.discount?.valueMinor ?? 0),
    0,
  );
  const taxableMinor = Math.max(0, subtotalMinor - lineDiscountMinor - order.orderDiscountMinor);
  const taxMinor = order.lines.reduce((acc, l) => {
    const lineNet = Math.max(0, l.qty * l.unitPriceMinor - (l.discount?.valueMinor ?? 0));
    return acc + Math.round(lineNet * l.taxRate);
  }, 0);
  const totalMinor = Math.max(0, taxableMinor + taxMinor);
  return { ...order, subtotalMinor, taxMinor, totalMinor };
}

export function tendersTotal(tenders: CashierTender[]): number {
  return tenders.reduce((acc, t) => acc + t.amountMinor, 0);
}

export function changeDue(totalMinor: number, tenders: CashierTender[]): number {
  return Math.max(0, tendersTotal(tenders) - totalMinor);
}

export function remainingDue(totalMinor: number, tenders: CashierTender[]): number {
  return Math.max(0, totalMinor - tendersTotal(tenders));
}

export function relativeMinutes(fromIso: string, now = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(fromIso).getTime()) / 60_000));
}

export function formatSessionDuration(minutes: number, locale: "tr" | "en" = "tr"): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (locale === "tr") return h > 0 ? `${h}sa ${m}dk` : `${m}dk`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function formatTimeShort(iso: string, locale: "tr" | "en" = "tr"): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(locale === "tr" ? "tr-TR" : "en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function isStockLow(item: CashierCatalogItem): boolean {
  return item.stockOnHand !== null && item.stockOnHand > 0 && item.stockOnHand <= 3;
}

export function isOutOfStock(item: CashierCatalogItem): boolean {
  return item.stockOnHand !== null && item.stockOnHand <= 0;
}

let _id = 1;
export function newLineId(): string {
  return `line-${Date.now()}-${_id++}`;
}

export function addItemToOrder(
  order: CashierOrder,
  item: CashierCatalogItem,
  qty = 1,
): CashierOrder {
  const existing = order.lines.find((l) => l.itemId === item.id && !l.discount);
  let lines: CashierOrderLine[];
  if (existing) {
    lines = order.lines.map((l) =>
      l.lineId === existing.lineId
        ? { ...l, qty: l.qty + qty, lineTotalMinor: (l.qty + qty) * l.unitPriceMinor }
        : l,
    );
  } else {
    const line: CashierOrderLine = {
      lineId: newLineId(),
      itemId: item.id,
      label: item.label,
      qty,
      unitPriceMinor: item.priceMinor,
      lineTotalMinor: qty * item.priceMinor,
      taxRate: item.taxRate,
      discount: null,
      note: null,
    };
    lines = [...order.lines, line];
  }
  return recomputeOrder({ ...order, lines });
}

export function updateLineQty(order: CashierOrder, lineId: string, qty: number): CashierOrder {
  if (qty <= 0) return removeLine(order, lineId);
  const lines = order.lines.map((l) =>
    l.lineId === lineId ? { ...l, qty, lineTotalMinor: qty * l.unitPriceMinor } : l,
  );
  return recomputeOrder({ ...order, lines });
}

export function removeLine(order: CashierOrder, lineId: string): CashierOrder {
  return recomputeOrder({ ...order, lines: order.lines.filter((l) => l.lineId !== lineId) });
}
